/* attentionTone.js — Unified Attention Model · single source of truth
 * across every Director Centre lens (RevenueLens, DriftDetection,
 * PerformanceCompare, InterventionAudit).
 *
 * Every operational lens MUST inherit from this palette so the Director
 * Centre feels like one connected operational intelligence platform.
 * Mirrors the backend `ATTENTION_LEVELS` constant in
 * `director_command_engine.py`.
 *
 * Visual discipline:
 *  • Critical  — calm rose (never bright red)
 *  • Elevated  — calm amber
 *  • Monitoring — operational violet
 *  • Stable    — quiet emerald
 */

export const ATTENTION_TONE = {
  Critical: {
    label: "Critical Attention",
    short: "Critical",
    bg: "rgba(244,63,94,0.12)",
    fg: "#9F1239",
    border: "rgba(244,63,94,0.25)",
    dot: "#F43F5E",
    glow: "rgba(244,63,94,0.20)",
  },
  Elevated: {
    label: "Elevated Attention",
    short: "Elevated",
    bg: "rgba(245,158,11,0.14)",
    fg: "#92400E",
    border: "rgba(245,158,11,0.28)",
    dot: "#F59E0B",
    glow: "rgba(245,158,11,0.20)",
  },
  Monitoring: {
    label: "Monitoring",
    short: "Monitoring",
    bg: "rgba(99,102,241,0.10)",
    fg: "#4338CA",
    border: "rgba(99,102,241,0.22)",
    dot: "#A78BFA",
    glow: "rgba(124,58,237,0.18)",
  },
  Stable: {
    label: "Stable",
    short: "Stable",
    bg: "rgba(16,185,129,0.10)",
    fg: "#047857",
    border: "rgba(16,185,129,0.22)",
    dot: "#10B981",
    glow: "rgba(16,185,129,0.18)",
  },
};

export const attentionToneFor = (level) =>
  ATTENTION_TONE[level] || ATTENTION_TONE.Stable;

/* Calm currency formatter aligned with the rest of the Director Centre. */
export const fmtGBP = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (v >= 1_000_000) return `£${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `£${Math.round(v / 1_000)}K`;
  if (v <= 0) return "£0";
  return `£${Math.round(v).toLocaleString()}`;
};

/* Calm short-form relative time used by the audit feed. */
export const fmtRelativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.round(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
